import React from 'react';
import { Formik } from 'formik';
import { object, string } from 'yup';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import { UpliftIndex } from '@/graphql/contracts/_generated_/contracts';
import { useUpdateUpliftIndex } from '../hooks/useUpdateUpliftIndex';

interface RenameUpliftIndexProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIndex: UpliftIndex;
  selectedRegionName: string | undefined;
  onSuccess: (updatedIndex: UpliftIndex) => void;
}

export default function RenameUpliftIndexModal({
  isOpen,
  onClose,
  selectedIndex,
  selectedRegionName,
  onSuccess,
}: RenameUpliftIndexProps) {
  const [updateUpliftIndex, { loading }] = useUpdateUpliftIndex(selectedRegionName);

  const validationSchema = object().shape({
    name: string().required('Name is required').max(255, 'Max length is 255'),
  });

  return (
    <AgModalWindow
      isOpen={isOpen}
      onClose={onClose}
      title="Rename Uplift Index"
      contentStyle={{ width: 340 }}
    >
      <Formik
        initialValues={{ name: selectedIndex.name }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const res = await updateUpliftIndex({
              variables: {
                input: {
                  id: selectedIndex.id!,
                  name: values.name,
                },
              },
            });
            const updatedIndex = res?.data?.updateUpliftIndex as UpliftIndex | undefined;
            if (updatedIndex) {
              onSuccess(updatedIndex);
            }
            onClose();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <ModalFormTextInput
              id="name"
              name="name"
              labelTitle="Name"
              requiredIcon={true}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              validationMessage={formik.touched.name && formik.errors.name ? (formik.errors.name as string) : undefined}
              className="mb-15"
              placeholder="Enter name"
            />
            <ActionButtonsWrapper
              leftButton={(
                <Button
                  buttonStyleType={ButtonStyleType.SECONDARY}
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              )}
              rightButton={(
                <Button
                  buttonStyleType={ButtonStyleType.PRIMARY}
                  type="submit"
                  disabled={!formik.isValid || formik.isSubmitting || loading}
                >
                  Save
                </Button>
              )}
            />
          </form>
        )}
      </Formik>
    </AgModalWindow>
  );
}
